import { useState, type SubmitEventHandler } from "react";
import { useLanguage } from "../i18n/LanguageContext";

type ContactModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

type FormStatus = "idle" | "loading" | "success" | "error";

const TRAFFIC_SOURCE_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
] as const;

/** Метки кампании и реферер: без них платный трафик не оценить. */
function collectTrafficSource(): Record<string, string> {
    if (typeof window === "undefined") {
        return {};
    }

    const searchParams = new URLSearchParams(window.location.search);
    const trafficSource: Record<string, string> = {};

    for (const key of TRAFFIC_SOURCE_KEYS) {
        const value = searchParams.get(key);
        if (value) {
            trafficSource[key] = value.slice(0, 200);
        }
    }

    if (document.referrer) {
        trafficSource.referrer = document.referrer.slice(0, 500);
    }

    return trafficSource;
}

export function ContactModal({ isOpen, onClose }: ContactModalProps) {
    const { locale, t } = useLanguage();
    const [status, setStatus] = useState<FormStatus>("idle");

    if (!isOpen) {
        return null;
    }

    const handleSubmit: SubmitEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        setStatus("loading");

        const form = event.currentTarget;
        const formData = new FormData(form);

        const payload = {
            name: String(formData.get("name") || "").trim(),
            phone: String(formData.get("phone") || "").trim(),
            comment: String(formData.get("comment") || "").trim(),
            // Согласие на обработку ПДн. Сервер проверяет его повторно: без этого
            // чекбокс был бы декоративным.
            consent: formData.get("consent") === "on",
            // Приманка для ботов: поле скрыто от человека, поэтому заполненное
            // значение означает автоматическую отправку.
            company: String(formData.get("company") || ""),
            ...collectTrafficSource(),
            language: locale,
        };

        try {
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Failed to send form");
            }

            setStatus("success");
            form.reset();
        } catch {
            setStatus("error");
        }
    };

    return (
        <div className="modalOverlay" onClick={onClose}>
            <div className="contactModal" onClick={(event) => event.stopPropagation()}>
                <button className="modalClose" type="button" onClick={onClose}>
                    ×
                </button>

                <p className="eyebrow">{t.contact.eyebrow}</p>

                <h2>{t.contact.title}</h2>

                <p className="modalLead">{t.contact.lead}</p>

                <form className="contactForm" onSubmit={handleSubmit}>
                    <label>
                        {t.contact.name}
                        <input
                            name="name"
                            type="text"
                            placeholder={t.contact.namePlaceholder}
                            required
                        />
                    </label>

                    <label>
                        {t.contact.phone}
                        <input
                            name="phone"
                            type="tel"
                            placeholder={t.contact.phonePlaceholder}
                            required
                        />
                    </label>

                    <label>
                        {t.contact.comment}
                        <textarea
                            name="comment"
                            placeholder={t.contact.commentPlaceholder}
                            rows={4}
                        />
                    </label>

                    {/* Приманка для ботов: скрыта от человека и от скринридера. */}
                    <input
                        className="honeypotField"
                        name="company"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                    />

                    <label className="consentRow">
                        <input name="consent" type="checkbox" required />
                        <span>
                            {t.contact.consentBefore}
                            <a href="/privacy.html" target="_blank" rel="noreferrer">
                                {t.contact.consentLink}
                            </a>
                            {t.contact.consentAfter}
                        </span>
                    </label>

                    <button className="primaryButton formSubmit" type="submit">
                        {status === "loading" ? t.contact.submitting : t.contact.submit}
                    </button>

                    {status === "success" && (
                        <p className="formMessage successMessage">{t.contact.success}</p>
                    )}

                    {status === "error" && (
                        <p className="formMessage errorMessage">{t.contact.error}</p>
                    )}
                </form>
            </div>
        </div>
    );
}
